import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, TextField, IconButton, Divider, Chip,
  Avatar, CircularProgress, Button, List, ListItem, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import SendIcon from '@mui/icons-material/Send';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import AddCommentIcon from '@mui/icons-material/AddComment';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PersonIcon from '@mui/icons-material/Person';
import { helpService, HelpConversation, HelpMessage, HelpFaq } from '../common/services/helpService';
import { TokenUserHelper } from '../common/services/token-user-service';
import { Inistitution } from './institution';

const POLL_INTERVAL_MS = 4000;

const senderColor = (type: number) => {
  if (type === 1) return '#e3f2fd';
  if (type === 2) return '#e8f5e9';
  return '#fff9c4';
};

const SenderIcon: React.FC<{ type: number }> = ({ type }) => {
  if (type === 1) return <SmartToyIcon sx={{ fontSize: 16, color: '#1976d2' }} />;
  if (type === 2) return <SupportAgentIcon sx={{ fontSize: 16, color: '#388e3c' }} />;
  return <PersonIcon sx={{ fontSize: 16, color: '#f57c00' }} />;
};

const statusColor = (status: number): 'default' | 'warning' | 'success' | 'info' | 'error' => {
  if (status === 1) return 'warning';
  if (status === 2) return 'success';
  if (status === 3) return 'info';
  return 'default';
};

export const SchoolHelpPage: React.FC = () => {
  // Left panel state
  const [conversations, setConversations] = useState<HelpConversation[]>([]);
  const [convLoading, setConvLoading] = useState(true);
  const [selectedConvId, setSelectedConvId] = useState<number | 'new' | null>(null);

  // Chat state
  const [faqs, setFaqs] = useState<HelpFaq[]>([]);
  const [activeConv, setActiveConv] = useState<HelpConversation | null>(null);
  const [messages, setMessages] = useState<HelpMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [chatPhase, setChatPhase] = useState<'faqs' | 'chat'>('faqs');

  // Callback dialog
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [callbackPhone, setCallbackPhone] = useState('');
  const [callbackNote, setCallbackNote] = useState('');
  const [submittingCallback, setSubmittingCallback] = useState(false);
  const [phoneOptions, setPhoneOptions] = useState<string[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastSentAtRef = useRef<string | undefined>(undefined);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scrollBottom = useCallback(() => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, []);

  // Load conversation list on mount
  useEffect(() => {
    setConvLoading(true);
    helpService.getConversations()
      .then(setConversations)
      .catch(() => {})
      .finally(() => setConvLoading(false));
  }, []);

  // Load FAQs on mount
  useEffect(() => {
    helpService.getFaqs().then(setFaqs).catch(() => {});
  }, []);

  // Polling for active conversation
  const poll = useCallback(async () => {
    if (!activeConv) return;
    try {
      const since = lastSentAtRef.current;
      const newMsgs = await helpService.getMessages(activeConv.conversationId, since);
      if (newMsgs.length > 0) {
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.messageId));
          const fresh = newMsgs.filter(m => !existingIds.has(m.messageId));
          if (fresh.length === 0) return prev;
          lastSentAtRef.current = fresh[fresh.length - 1].sentAt;
          return [...prev, ...fresh];
        });
        scrollBottom();
      }
    } catch {}
  }, [activeConv, scrollBottom]);

  useEffect(() => {
    if (!activeConv || activeConv.status === 0 || activeConv.status === 4) {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      return;
    }
    pollTimerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
  }, [activeConv, poll]);

  const selectConversation = async (conv: HelpConversation) => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    setSelectedConvId(conv.conversationId);
    setChatPhase('chat');
    setInputText('');
    try {
      const full = await helpService.getConversation(conv.conversationId);
      if (full) {
        setActiveConv(full);
        setMessages(full.messages);
        if (full.messages.length > 0)
          lastSentAtRef.current = full.messages[full.messages.length - 1].sentAt;
        scrollBottom();
      }
    } catch {}
  };

  const startNewConversation = () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    setSelectedConvId('new');
    setActiveConv(null);
    setMessages([]);
    setInputText('');
    setChatPhase('faqs');
    lastSentAtRef.current = undefined;
  };

  const startChat = async (message: string) => {
    setSending(true);
    try {
      const conv = await helpService.startConversation(message);
      setActiveConv(conv);
      setMessages(conv.messages);
      setConversations(prev => [conv, ...prev]);
      setSelectedConvId(conv.conversationId);
      if (conv.messages.length > 0)
        lastSentAtRef.current = conv.messages[conv.messages.length - 1].sentAt;
      setChatPhase('chat');
      scrollBottom();
    } catch {
    } finally {
      setSending(false);
    }
  };

  const handleStartChatManually = () => {
    if (!inputText.trim()) return;
    const msg = inputText.trim();
    setInputText('');
    startChat(msg);
  };

  const sendMessage = async () => {
    if (!inputText.trim() || !activeConv || sending) return;
    const text = inputText.trim();
    setInputText('');
    setSending(true);
    try {
      const msg = await helpService.sendMessage(activeConv.conversationId, text);
      if (msg) {
        setMessages(prev => [...prev, msg]);
        lastSentAtRef.current = msg.sentAt;
        scrollBottom();
      }
    } catch {
    } finally {
      setSending(false);
    }
  };

  const handleCallbackOpen = async () => {
    setCallbackOpen(true);
    if (phoneOptions.length === 0) {
      const tokenUser = TokenUserHelper.Get();
      if (tokenUser?.UserId) {
        const options = await helpService.getSchoolPhones(tokenUser.UserId);
        setPhoneOptions(options);
        if (options.length > 0 && !callbackPhone) {
          setCallbackPhone(options[0].replace(/^.*–\s*/, '').trim());
        }
      }
    }
  };

  const submitCallback = async () => {
    if (!callbackPhone.trim() || !activeConv) return;
    setSubmittingCallback(true);
    try {
      await helpService.requestCallback(activeConv.conversationId, callbackPhone.trim(), callbackNote.trim() || undefined);
      setActiveConv(prev => prev ? { ...prev, status: 3, statusLabel: 'Callback Requested' } : prev);
      setCallbackOpen(false);
    } catch {
    } finally {
      setSubmittingCallback(false);
    }
  };

  const isResolved = activeConv?.status === 4;
  const isCallbackRequested = activeConv?.status === 3;

  // ── Left panel ────────────────────────────────────────────────────────────
  const ConversationList = (
    <Box sx={{ width: 280, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Button
          fullWidth
          variant="contained"
          size="small"
          startIcon={<AddCommentIcon />}
          onClick={startNewConversation}
          disableElevation
        >
          New Conversation
        </Button>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {convLoading && <CircularProgress size={24} sx={{ display: 'block', mx: 'auto', mt: 3 }} />}
        {!convLoading && conversations.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 4 }}>
            No conversations yet.
          </Typography>
        )}
        <List dense disablePadding>
          {conversations.map(conv => {
            const isActive = selectedConvId === conv.conversationId;
            return (
              <ListItem
                key={conv.conversationId}
                disablePadding
                onClick={() => selectConversation(conv)}
                sx={{
                  px: 1.5, py: 1, cursor: 'pointer',
                  bgcolor: isActive ? 'primary.50' : 'transparent',
                  borderLeft: isActive ? '3px solid' : '3px solid transparent',
                  borderLeftColor: isActive ? 'primary.main' : 'transparent',
                  '&:hover': { bgcolor: isActive ? 'primary.50' : 'action.hover' },
                  transition: 'background 0.15s',
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                    <Chip
                      label={conv.statusLabel}
                      size="small"
                      color={statusColor(conv.status)}
                      sx={{ height: 16, fontSize: 10 }}
                    />
                    <Typography variant="caption" color="text.secondary" fontSize={10}>
                      {new Date(conv.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.primary"
                    sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {conv.messages?.[0]?.message ?? '(no messages)'}
                  </Typography>
                </Box>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );

  // ── Right panel ───────────────────────────────────────────────────────────
  const ChatPanel = (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%' }}>
      {/* Chat header */}
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
          <SmartToyIcon sx={{ fontSize: 20 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {selectedConvId === 'new' || selectedConvId === null ? 'New Conversation' : 'eAbhyas Support'}
          </Typography>
          {activeConv && (
            <Chip
              label={activeConv.statusLabel}
              size="small"
              color={statusColor(activeConv.status)}
              sx={{ height: 16, fontSize: 10 }}
            />
          )}
        </Box>
      </Box>

      {/* Messages / FAQ area */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {selectedConvId === null && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
            <SmartToyIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
            <Typography variant="body2">Select a conversation or start a new one</Typography>
          </Box>
        )}

        {(selectedConvId === 'new') && chatPhase === 'faqs' && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Hi! How can I help you today? Pick a topic below or type your question.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {faqs.map(faq => (
                <Chip
                  key={faq.faqId}
                  label={faq.question}
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => startChat(faq.question)}
                  sx={{ cursor: 'pointer', height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                />
              ))}
            </Box>
            {sending && <CircularProgress size={20} sx={{ mt: 2 }} />}
          </Box>
        )}

        {chatPhase === 'chat' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {messages.map(msg => (
              <Box
                key={msg.messageId}
                sx={{ alignSelf: msg.senderType === 0 ? 'flex-end' : 'flex-start', maxWidth: '72%' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                  <SenderIcon type={msg.senderType} />
                  <Typography variant="caption" color="text.secondary" fontSize={10}>
                    {msg.senderName} · {new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
                <Paper
                  variant="outlined"
                  sx={{ px: 1.5, py: 1, bgcolor: senderColor(msg.senderType), borderRadius: 2, borderColor: 'transparent' }}
                >
                  <Typography variant="body2" sx={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {msg.message}
                  </Typography>
                </Paper>
              </Box>
            ))}
            {sending && <CircularProgress size={18} sx={{ alignSelf: 'flex-start' }} />}
            {activeConv?.resolutionNote && (
              <Box sx={{ mt: 1, px: 2, py: 1, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
                <Typography variant="caption" color="success.dark">
                  <strong>Resolution:</strong> {activeConv.resolutionNote}
                </Typography>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>

      <Divider />

      {/* Footer input */}
      {(selectedConvId === 'new' || selectedConvId === null) && chatPhase === 'faqs' && selectedConvId !== null && (
        <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
          <Autocomplete
            fullWidth
            freeSolo
            options={faqs.map(f => f.question)}
            inputValue={inputText}
            onInputChange={(_e, value) => setInputText(value)}
            onChange={(_e, value) => { if (value) { setInputText(''); startChat(value); } }}
            filterOptions={(options, { inputValue }) =>
              inputValue.trim() ? options.filter(o => o.toLowerCase().includes(inputValue.toLowerCase())) : []
            }
            disabled={sending}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                placeholder="Type your question…"
                onKeyDown={e => { if (e.key === 'Enter' && !e.defaultPrevented) handleStartChatManually(); }}
              />
            )}
          />
          <IconButton color="primary" onClick={handleStartChatManually} disabled={!inputText.trim() || sending}>
            <SendIcon />
          </IconButton>
        </Box>
      )}

      {selectedConvId === 'new' && chatPhase === 'faqs' && (
        <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
          <Autocomplete
            fullWidth freeSolo
            options={faqs.map(f => f.question)}
            inputValue={inputText}
            onInputChange={(_e, value) => setInputText(value)}
            onChange={(_e, value) => { if (value) { setInputText(''); startChat(value); } }}
            filterOptions={(options, { inputValue }) =>
              inputValue.trim() ? options.filter(o => o.toLowerCase().includes(inputValue.toLowerCase())) : []
            }
            disabled={sending}
            renderInput={params => (
              <TextField
                {...params} size="small"
                placeholder="Type your question…"
                onKeyDown={e => { if (e.key === 'Enter' && !e.defaultPrevented) handleStartChatManually(); }}
              />
            )}
          />
          <IconButton color="primary" onClick={handleStartChatManually} disabled={!inputText.trim() || sending}>
            <SendIcon />
          </IconButton>
        </Box>
      )}

      {chatPhase === 'chat' && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {isResolved && (
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.75, textAlign: 'center' }}>
              This conversation has been resolved.
            </Typography>
          )}
          {isCallbackRequested && (
            <Typography variant="caption" color="info.main" sx={{ px: 2, py: 0.75, textAlign: 'center' }}>
              Callback requested — our team will call you shortly.
            </Typography>
          )}
          {!isResolved && !isCallbackRequested && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
              <IconButton size="small" title="Request a callback" onClick={handleCallbackOpen} color="info">
                <PhoneCallbackIcon fontSize="small" />
              </IconButton>
              <TextField
                fullWidth size="small" placeholder="Type a message…"
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }}
                disabled={sending}
              />
              <IconButton color="primary" onClick={sendMessage} disabled={!inputText.trim() || sending}>
                <SendIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Inistitution>
      <Box sx={{ display: 'flex', height: '100%', bgcolor: 'background.paper', borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
        {ConversationList}
        {ChatPanel}
      </Box>

      {/* Callback Dialog */}
      <Dialog open={callbackOpen} onClose={() => setCallbackOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Request a Callback</DialogTitle>
        <DialogContent>
          <Autocomplete
            freeSolo
            options={phoneOptions}
            inputValue={callbackPhone}
            onInputChange={(_e, value) => {
              setCallbackPhone(value.includes('–') ? value.replace(/^.*–\s*/, '').trim() : value);
            }}
            onChange={(_e, value) => {
              if (value) setCallbackPhone(value.includes('–') ? value.replace(/^.*–\s*/, '').trim() : value);
            }}
            renderInput={params => (
              <TextField {...params} fullWidth label="Phone Number" size="small" sx={{ mt: 1 }} placeholder="Select or enter a phone number" />
            )}
          />
          <TextField
            fullWidth label="Brief note (optional)" value={callbackNote}
            onChange={e => setCallbackNote(e.target.value)}
            sx={{ mt: 2 }} size="small" multiline rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCallbackOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCallback} disabled={!callbackPhone.trim() || submittingCallback}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Inistitution>
  );
};
